import { DOZER_CONFIG } from '../../config/dozer.config';
import { DOZER_RIG } from '../../dozer/dozerRig';
import { GreyboxMaterialId as M, type GreyboxPart, type GreyboxRigNode } from '../AssetTypes';
import { box, cylinder } from './greybox';

/**
 * Greybox of VEH_DOZER_A (fallback of the published GLB): simple yellow and black boxes and cylinders on the rig of
 * the GLB – the same node names (dozer/dozerRig.ts) at the same rest positions (tools/blender/assets/
 * VEH_05_Catapillar.py, game space: x right, y up, z forward, origin = ground centre of the tracks), so the
 * DozerPresenter drives both alike. L = −x.
 */
type Triple = readonly [number, number, number];

const RAD_TO_DEG = 180 / Math.PI;
const SIDES = [
  { side: 'L', sign: -1 },
  { side: 'R', sign: 1 },
] as const;

/** Rest positions of the rig (game space). */
const PIVOT_BLADE: Triple = [0, 0.33, 0.42];
const BLADE_TIP: Triple = [0, 0.03, 2.08];
const CYLINDER = { x: 0.5, bodyEye: [1.18, 0.8] as const, bladeEye: [0.683, 1.3] as const, barrel: { diameter: 0.13, length: 0.36 }, rod: { diameter: 0.07, length: 0.4 } };
const TRACK = { x: 0.71, width: 0.28, zFrom: -1.93, zTo: 0.79, top: 0.79, bottom: 0.01 };
const SPROCKET = { y: 0.4, z: -1.54, diameter: 0.48 };
const ROLLERS = { y: 0.2, z: [-0.6, -0.27, 0.12], diameter: 0.2 };
/** Hubs sit on the outside of the belts. */
const HUB_X = TRACK.x + TRACK.width / 2 + 0.04;
const HUB_WIDTH = 0.08;

/** Pitch (degrees about x) that turns local +y along (dy, dz) – Babylon: (0, 1, 0) → (0, cos a, sin a). */
const pitchTowards = (dy: number, dz: number): number => Math.atan2(dz, dy) * RAD_TO_DEG;

/** Cylinder along its local +y, starting at `from` (asset space) – its axis turned by `pitch` about x. */
function cylinderFrom(node: string, from: Triple, pitch: number, diameter: number, length: number, material: M): GreyboxPart {
  const a = pitch / RAD_TO_DEG;
  const dir: Triple = [0, Math.cos(a), Math.sin(a)];
  const center: Triple = [from[0], from[1] + (dir[1] * length) / 2, from[2] + (dir[2] * length) / 2];
  return { shape: 'cylinder', size: [diameter, length, diameter], position: center, rotation: [pitch, 0, 0], material, node };
}

/** Wheel hub (cylinder across the track, axis x) of a rig node. */
function hub(node: string, x: number, y: number, z: number, diameter: number): GreyboxPart {
  return { shape: 'cylinder', size: [diameter, HUB_WIDTH, diameter], position: [x, y, z], rotation: [0, 0, 90], material: M.MachineYellow, node };
}

export function dozerGreybox(): { readonly parts: GreyboxPart[]; readonly rig: GreyboxRigNode[] } {
  const n = DOZER_RIG;
  const seat = DOZER_CONFIG.vehicle.seat;
  const rig: GreyboxRigNode[] = [
    { name: n.root, position: [0, 0, 0] },
    { name: n.body, parent: n.root, position: [0, 0, 0] },
    { name: n.pivotBlade, parent: n.root, position: PIVOT_BLADE },
    { name: n.blade, parent: n.pivotBlade, position: PIVOT_BLADE },
    { name: n.bladeTip, parent: n.pivotBlade, position: BLADE_TIP },
    { name: n.seat, parent: n.root, position: [seat.x, seat.y, seat.z] },
    { name: n.exhaust, parent: n.root, position: [-0.35, 1.8, -0.2] },
  ];
  const parts: GreyboxPart[] = [];
  const [eyeY, eyeZ] = CYLINDER.bodyEye;
  const [rodY, rodZ] = CYLINDER.bladeEye;
  const down = pitchTowards(rodY - eyeY, rodZ - eyeZ);
  const up = pitchTowards(eyeY - rodY, eyeZ - rodZ);
  for (const { side, sign } of SIDES) {
    const barrel = n.cylinderBarrel(side);
    const rod = n.cylinderRod(side);
    const x = sign * CYLINDER.x;
    rig.push(
      { name: barrel, parent: n.root, position: [x, eyeY, eyeZ], rotation: [down, 0, 0] },
      { name: rod, parent: n.pivotBlade, position: [x, rodY, rodZ], rotation: [up, 0, 0] },
      { name: n.track(side), parent: n.root, position: [0, 0, 0] },
      { name: `${n.wheelPrefix}SPROCKET_${side}`, parent: n.root, position: [sign * TRACK.x, SPROCKET.y, SPROCKET.z] },
      ...ROLLERS.z.map((z, i): GreyboxRigNode => ({ name: `${n.wheelPrefix}ROLLER_${side}${i + 1}`, parent: n.root, position: [sign * TRACK.x, ROLLERS.y, z] })),
    );
    parts.push(
      cylinderFrom(barrel, [x, eyeY, eyeZ], down, CYLINDER.barrel.diameter, CYLINDER.barrel.length, M.MachineYellow),
      cylinderFrom(rod, [x, rodY, rodZ], up, CYLINDER.rod.diameter, CYLINDER.rod.length, M.Steel),
      {
        ...box([TRACK.width, TRACK.top - TRACK.bottom, TRACK.zTo - TRACK.zFrom], sign * TRACK.x, TRACK.bottom, (TRACK.zFrom + TRACK.zTo) / 2, M.DozerTrack),
        node: n.track(side),
        bandTile: DOZER_CONFIG.vehicle.trackTileLength,
      },
      hub(`${n.wheelPrefix}SPROCKET_${side}`, sign * HUB_X, SPROCKET.y, SPROCKET.z, SPROCKET.diameter),
      ...ROLLERS.z.map((z, i) => hub(`${n.wheelPrefix}ROLLER_${side}${i + 1}`, sign * HUB_X, ROLLERS.y, z, ROLLERS.diameter)),
      // push arm beside the track, from the trunnion to the blade
      { ...box([0.12, 0.16, 1.55], sign * 0.93, 0.25, 1.15, M.MachineYellow), node: n.blade },
      // side console with the travel lever beside the seat
      { ...box([0.3, 0.45, 0.6], sign * 0.62, 0.8, -1.0, M.MachineYellow), node: n.body },
      { ...cylinder(0.05, 0.55, sign * 0.18, 1.5, -0.42, M.RubberBlack), node: n.body },
    );
  }
  parts.push(
    // body: deck over the tracks, rear housing, operator platform, seat, hood with grille and exhaust stack
    { ...box([1.6, 0.2, 3.3], 0, 0.6, -0.35, M.MachineYellow), node: n.body },
    { ...box([1.55, 0.55, 0.75], 0, 0.8, -1.72, M.MachineYellow), node: n.body },
    { ...box([1.1, 0.04, 0.55], 0, 0.95, -0.5, M.RubberBlack), node: n.body },
    { ...box([0.5, 0.56, 0.45], 0, 0.8, seat.z, M.RubberBlack), node: n.body },
    { ...box([0.52, 0.12, 0.5], 0, seat.y - 0.12, seat.z, M.RubberBlack), node: n.body },
    { ...box([0.5, 0.55, 0.1], 0, seat.y, seat.z - 0.28, M.RubberBlack), node: n.body },
    { ...box([1.05, 0.95, 1.55], 0, 0.7, 0.55, M.MachineYellow), node: n.body },
    { ...box([0.6, 0.5, 0.04], 0, 0.95, 1.33, M.RubberBlack), node: n.body },
    { ...cylinder(0.1, 0.25, -0.35, 1.6, -0.2, M.RubberBlack), node: n.body },
    // blade: mouldboard, cutting edge, cross brace (the blade tip socket sits on the edge)
    { ...box([2.34, 0.88, 0.16], 0, 0.02, 1.98, M.MachineYellow), node: n.blade },
    { ...box([2.34, 0.06, 0.06], 0, 0, 2.07, M.RubberBlack), node: n.blade },
    { ...box([1.9, 0.12, 0.12], 0, 0.3, 1.85, M.MachineYellow), node: n.blade },
  );
  return { parts, rig };
}
